import { defaultOnboardingMetadata } from "../components/edit-chat/utils/stateHelpers";
import { AUDIO_LANGUAGES } from "./constants";
import {
  AudioLanguage,
  ColorScheme,
  SurveyConfig,
  SurveyConfigOverride,
} from "./types";
import type {
  LanguageToTTSConfig,
  SurveyData,
  SurveyDataSingleLanguage,
  SurveyFlags,
} from "@/backend/db/drizzle/types/jsonTypes";
import { getRegionalVoiceLanguages } from "@/lib/tts/regionalVoices";
import {
  getDefaultVoiceForAudioLanguage,
  listAllowedTtsModels,
  voiceToTTSConfig,
} from "@/lib/tts/utils";
import type { CompliancePolicy } from "@/lib/vendor-policy/types";
import { notNull } from "@/utils/helpers";
import type { LocalizationRegion } from "@/utils/localizationRegion";

export const CHAT_USE_THINKING = false;

export const DEFAULT_SURVEY_FLAGS: SurveyFlags = {
  auto_hide_partial_responses: true,
};

// Default voices are built under the baseline contractual requirements used
// by the product. Scope-specific policy is applied when configuration is
// resolved for an organization or study.
// TODO(soon): make default config policy-aware
export const DEFAULT_TTS_POLICY: CompliancePolicy = {
  trainingPolicy: "not_allowed",
  requireZdr: true,
  requireBaa: true,
  bannedProviders: [],
};

const defaultTtsModels = listAllowedTtsModels(DEFAULT_TTS_POLICY);

const defaultLanguageToTTSConfig = (
  Object.keys(AUDIO_LANGUAGES) as AudioLanguage[]
).reduce<LanguageToTTSConfig>((acc, lang) => {
  const voice = getDefaultVoiceForAudioLanguage({
    language: lang,
    allowedModels: defaultTtsModels,
  });
  if (!voice) {
    return acc;
  }
  acc[lang] = voiceToTTSConfig({
    voice,
  });
  return acc;
}, {});

export const default_survey_config: SurveyConfig = {
  is_screen_recording: false,
  primary: "#000000",
  secondary: "#0021CC",
  background: "#FFFFFF",
  logo: undefined,
  disable_live_sheets: false,
  stop_chatting_at_100: true,
  idle_time_to_complete_s: 60 * 15,
  hide_input_on_welcome: true,
  restore_session_allow_restart: false,
  allowMultipleResponses: false,
  voice_first: true,
  voice_disabled: false,
  voice_only: false,
  is_video_interview: true,
  disable_skip: true,
  disable_history: false,
  available_languages: ["en"],
  question_language: "en",
  analysis_language: "en",
  ask_for_language: false,
  transcription_provider: "assemblyai",
  urlParams: {},
  accessibility_check: false,
  temperature: 0.0,
  redirect_submit_text_override: undefined, // if empty then it will be set in language settings
  redact_pii: false,
  redact_mnpi: false,
  label_speakers: false,
  enable_video_quality_check: false,
  enable_markdown: true,
  enable_auto_advance: false,
  disableEmotionExtraction: false,
  onboardingMetadata: defaultOnboardingMetadata("skip_onboarding"),
  text: {
    input_continue_chatting:
      "Interview completed. Type here to correct or elaborate.",
    input_type_option: "Type other option here",
    input_placeholder_mobile: "Answer here...",
    input_placeholder_desktop: "Answer here or press Enter ↵ to skip",
  },
  ttsConfig: {
    isEnabled: true,
    defaultIsMuted: true,
    languageToConfig: defaultLanguageToTTSConfig,
  },
};

export const update_config = (
  base_config: SurveyConfig,
  override: SurveyConfigOverride
): SurveyConfig => {
  const res = {
    ...base_config,
    ...override,
    text: { ...base_config.text, ...override.text },
    ttsConfig: {
      ...base_config.ttsConfig,
      ...override.ttsConfig,
      languageToConfig: {
        ...base_config.ttsConfig?.languageToConfig,
        ...override.ttsConfig?.languageToConfig,
      },
    },
  };
  if ("default_language" in res) {
    // for backwards compatibility
    const lang = res["default_language"] as AudioLanguage;
    res.available_languages = [lang];
    res.question_language = lang;
    delete res["default_language"];
  }
  return res;
};

export const get_config = (override: SurveyConfigOverride): SurveyConfig => {
  return update_config(default_survey_config, override);
};

// The base config at creation; stored or org overrides layered on top still win.
export const getRegionalDefaultConfig = ({
  region,
}: {
  region: LocalizationRegion | null;
}): SurveyConfig => {
  if (region === null) {
    return default_survey_config;
  }
  const regionalLanguageToConfig: LanguageToTTSConfig = Object.fromEntries(
    getRegionalVoiceLanguages(region).flatMap((language) => {
      const regionalVoice = getDefaultVoiceForAudioLanguage({
        language,
        allowedModels: defaultTtsModels,
        region,
      });
      return notNull(regionalVoice)
        ? [[language, voiceToTTSConfig({ voice: regionalVoice })]]
        : [];
    })
  );
  return update_config(default_survey_config, {
    ttsConfig: { languageToConfig: regionalLanguageToConfig },
  });
};

// Ideally we'd check whether any response actually has video/screen data,
// but that requires a DB query. This data/config-based check is a fast proxy.
// A study can produce clips if any of:
//   - the whole interview is video (is_video_interview)
//   - the whole interview is screen-recording (is_screen_recording)
//   - any individual question asks for screen recording as its preferred input
//     (preferredInput === "screenRecording"), which is supported even on
//     otherwise audio-only studies.
// Pass `data` whenever possible; omitting it only checks the study-level flags.
// The two signals MediaInput.ts uses to decide whether a turn records the screen.
// Pass `data` whenever possible; omitting it only checks the study-level flag.
export const recordsScreen = (
  config: SurveyConfigOverride,
  data?: SurveyDataSingleLanguage | SurveyData | null
): boolean => {
  if (config.is_screen_recording === true) return true;
  if (!data) return false;
  return data.blocks.some((block) =>
    block.items.some(
      (item) =>
        item.type === "question" && item.preferredInput === "screenRecording"
    )
  );
};

export const supportsClips = (
  config: SurveyConfigOverride,
  data?: SurveyDataSingleLanguage | SurveyData | null
): boolean => config.is_video_interview === true || recordsScreen(config, data);

// Voice answers but no camera or screen: its clips can only be sound cards (gated by the audio-reels flag).
export const isAudioOnlyStudy = (
  config: SurveyConfigOverride,
  data?: SurveyDataSingleLanguage | SurveyData | null
): boolean => config.voice_disabled !== true && !supportsClips(config, data);

// Client twin of getStudySupportsClips: the caller reads the audio-reels flag, since the typed SDK is server only.
export const supportsClipsWithAudioReels = (
  config: SurveyConfigOverride,
  data: SurveyDataSingleLanguage | SurveyData | null | undefined,
  isAudioReelsEnabled: boolean
): boolean =>
  supportsClips(config, data) ||
  (isAudioReelsEnabled && isAudioOnlyStudy(config, data));

// TODO(soon): change this color scheme
export const DEFAULT_COLOR_SCHEME: ColorScheme = {
  accent: default_survey_config.secondary,
  palette: [default_survey_config.secondary, "#E5E5E5"],
};
